// 生词本单词列表组件
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  message,
  Spin,
  Space,
  Modal,
  Input,
  Popconfirm,
  Form,
  Select,
  Grid,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  SoundOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  getVocabularyListWords,
  updateWordNote,
  deleteVocabularyListWord,
  deleteVocabularyListWords,
  exportVocabularyList,
} from '@/libs/api';
import { handleAddToVocabularyList } from '@/utils/vocabulary';
import WordDetail from './WordDetail';
import styles from '@/assets/styles/WordList.module.less';
import type { ColumnsType, TableProps } from 'antd/es/table';
import type { components } from '@/api/types';

interface WordDataType {
  word: string;
  phonetic?: string;
  translations: Array<{
    pos?: string;
    meanings: string[];
  }>;
  example?: string;
  dictId?: number;
  aiTranslation?: string;
  aiTranslationId?: number;
}

interface VocabularyWord {
  id: number;
  word: string;
  wordData: WordDataType;
  translation: string | null;
  note: string | null;
  addedAt: string;
  vocabularyListId: number;
}

type ApiVocabularyWord = components['schemas']['VocabularyWord'];

const normalizeTranslation = (translation: unknown): WordDataType['translations'][number] => {
  if (Array.isArray(translation)) {
    const [pos, ...meaningList] = translation;
    return {
      pos: typeof pos === 'string' ? pos : undefined,
      meanings: meaningList.map(item => String(item)).filter(Boolean),
    };
  }

  if (translation && typeof translation === 'object') {
    const { pos, meanings } = translation as { pos?: unknown; meanings?: unknown[] };
    return {
      pos: typeof pos === 'string' ? pos : undefined,
      meanings: Array.isArray(meanings) ? meanings.map(item => String(item)) : [],
    };
  }

  return { pos: undefined, meanings: [] };
};

const transformWordData = (word: ApiVocabularyWord): VocabularyWord => {
  const translations = Array.isArray(word.wordData?.translations)
    ? word.wordData?.translations
    : [];

  return {
    id: word.id,
    word: word.word,
    wordData: {
      word: word.wordData?.word || word.word,
      phonetic: word.wordData?.phonetic,
      translations: translations.map(normalizeTranslation),
      example: word.wordData?.example,
      dictId: word.wordData?.dictId,
      aiTranslation: word.wordData?.aiTranslation,
      aiTranslationId: word.wordData?.aiTranslationId,
    },
    translation: word.translation ?? null,
    note: word.note ?? null,
    addedAt: word.addedAt,
    vocabularyListId: word.vocabularyListId,
  };
};

const { useBreakpoint } = Grid;

const WordList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [loading, setLoading] = useState(false);
  const [words, setWords] = useState<VocabularyWord[]>([]);
  const [editingWord, setEditingWord] = useState<VocabularyWord | null>(null);
  const [editNote, setEditNote] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState<number>(-1);
  const [addWordModalVisible, setAddWordModalVisible] = useState(false);
  const [form] = Form.useForm();

  // 添加词性选项
  const posOptions = [
    { value: 'n.', label: '名词 (n.)' },
    { value: 'v.', label: '动词 (v.)' },
    { value: 'adj.', label: '形容词 (adj.)' },
    { value: 'adv.', label: '副词 (adv.)' },
    { value: 'prep.', label: '介词 (prep.)' },
    { value: 'conj.', label: '连词 (conj.)' },
    { value: 'pron.', label: '代词 (pron.)' },
    { value: 'art.', label: '冠词 (art.)' },
    { value: 'num.', label: '数词 (num.)' },
    { value: 'int.', label: '感叹词 (int.)' },
    { value: 'aux.', label: '助动词 (aux.)' },
  ];

  const fetchWords = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await getVocabularyListWords(parseInt(id));
      const transformedWords = (response || []).map(transformWordData);
      setWords(transformedWords);
    } catch (error) {
      message.error('获取单词列表失败');
      console.error('Fetch words error:', error);
      setWords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWords();
  }, [id]);

  const handleEditNote = async () => {
    if (!editingWord) return;
    try {
      await updateWordNote(editingWord.id, editNote);
      message.success('笔记更新成功');
      setEditingWord(null);
      fetchWords();
    } catch (error) {
      message.error('更新笔记失败');
      console.error('Update note error:', error);
    }
  };

  const handleDeleteWord = async (wordId: number) => {
    if (!id) return;
    try {
      await deleteVocabularyListWord(parseInt(id), wordId);
      message.success('删除成功');
      fetchWords();
    } catch (error) {
      message.error('删除失败');
      console.error('Delete word error:', error);
    }
  };

  const handleBatchDelete = async () => {
    if (!id || selectedRowKeys.length === 0) return;
    try {
      await deleteVocabularyListWords(parseInt(id), {
        wordIds: selectedRowKeys.map(key => Number(key)),
      });
      message.success('批量删除成功');
      setSelectedRowKeys([]);
      fetchWords();
    } catch (error) {
      message.error('批量删除失败');
      console.error('Batch delete words error:', error);
    }
  };

  const handleExport = async () => {
    if (!id) return;
    try {
      const blob = await exportVocabularyList(parseInt(id));
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vocabulary-list-${id}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('导出成功');
    } catch (error) {
      message.error('导出失败');
      console.error('Export vocabulary list error:', error);
    }
  };

  const handleWordClick = (record: VocabularyWord) => {
    const index = words.findIndex(w => w.id === record.id);
    setCurrentWordIndex(index);
    setDetailVisible(true);
  };

  const handlePrevWord = () => {
    if (currentWordIndex > 0) {
      setCurrentWordIndex(currentWordIndex - 1);
    }
  };

  const handleNextWord = () => {
    if (currentWordIndex < words.length - 1) {
      setCurrentWordIndex(currentWordIndex + 1);
    }
  };

  const handleUpdateNote = async (wordId: number, note: string) => {
    try {
      await updateWordNote(wordId, note);
      await fetchWords();
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const handleAddWord = async (values: {
    word: string;
    phonetic?: string;
    pos?: string;
    meanings?: string;
    example?: string;
    note?: string;
  }) => {
    if (!id) return;
    try {
      // 处理释义，将输入的释义按分号分割成数组
      const meanings = values.meanings
        ? values.meanings.split('；').filter(meaning => meaning.trim())
        : [];

      const success = await handleAddToVocabularyList(parseInt(id), {
        content: values.word,
        source: 'manual',
        translations: [
          {
            pos: values.pos,
            meanings: meanings,
          },
        ],
        phonetic: values.phonetic,
        example: values.example,
      });

      if (success) {
        message.success('添加成功');
        setAddWordModalVisible(false);
        form.resetFields();
        fetchWords();
      }
    } catch (error) {
      message.error('添加失败');
      console.error('Add word error:', error);
    }
  };

  // 动态列配置
  const getColumns = (): ColumnsType<VocabularyWord> => {
    const baseColumns: ColumnsType<VocabularyWord> = [
      {
        title: '单词',
        dataIndex: ['wordData', 'word'],
        key: 'word',
        fixed: 'left',
        width: screens.xs ? 80 : 120,
        render: (text: string, record: VocabularyWord) => (
          <a onClick={() => handleWordClick(record)}>
            <strong style={{ fontSize: screens.xs ? '12px' : '14px' }}>{text}</strong>
          </a>
        ),
      },
    ];

    // 平板及以上显示读音
    if (screens.md) {
      baseColumns.push({
        title: '读音',
        dataIndex: ['wordData', 'phonetic'],
        key: 'phonetic',
        width: 100,
        render: (text: string | undefined) => <span>{text || '-'}</span>,
      });
    }

    // 平板及以上显示释义
    if (screens.md) {
      baseColumns.push({
        title: '释义',
        dataIndex: ['wordData', 'translations'],
        key: 'translations',
        width: screens.lg ? 200 : 150,
        render: (translations: WordDataType['translations']) => {
          if (!translations?.length) return <span>-</span>;
          return (
            <div>
              {translations.slice(0, 1).map((trans, index) => (
                <div key={index} style={{ lineHeight: '1.4' }}>
                  {trans.pos && (
                    <span style={{ color: '#666', marginRight: '4px', fontSize: '12px' }}>
                      {trans.pos}
                    </span>
                  )}
                  <span style={{ color: '#333', fontSize: '12px' }}>
                    {trans.meanings.length > 0
                      ? trans.meanings.slice(0, 2).join('；') +
                        (trans.meanings.length > 2 ? '...' : '')
                      : '-'}
                  </span>
                </div>
              ))}
            </div>
          );
        },
      });
    }

    // 桌面端显示例句
    if (screens.lg) {
      baseColumns.push({
        title: '例句',
        dataIndex: ['wordData', 'example'],
        key: 'example',
        width: 150,
        render: (text: string | undefined) => (
          <span style={{ fontSize: '12px' }}>
            {text ? (text.length > 20 ? text.slice(0, 20) + '...' : text) : '-'}
          </span>
        ),
      });
    }

    // 笔记列（始终显示）
    baseColumns.push({
      title: '笔记',
      dataIndex: 'note',
      key: 'note',
      width: screens.xs ? 80 : 120,
      render: (text: string | null, record: VocabularyWord) => (
        <Space size="small">
          <span style={{ fontSize: screens.xs ? '11px' : '12px' }}>
            {text ? (text.length > 10 ? text.slice(0, 10) + '...' : text) : '-'}
          </span>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={e => {
              e.stopPropagation();
              setEditingWord(record);
              setEditNote(record.note || '');
            }}
            style={{
              color: '#6a0dad',
              padding: '2px 4px',
              height: 'auto',
              fontSize: '12px',
            }}
          >
            {screens.xs ? '' : '编辑'}
          </Button>
        </Space>
      ),
    });

    // 桌面端显示添加时间
    if (screens.lg) {
      baseColumns.push({
        title: '时间',
        dataIndex: 'addedAt',
        key: 'addedAt',
        width: 100,
        render: (text: string) => (
          <span style={{ fontSize: '12px' }}>
            {new Date(text).toLocaleDateString('zh-CN', { month: 'numeric', day: 'numeric' })}
          </span>
        ),
      });
    }

    // 操作列
    baseColumns.push({
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: screens.xs ? 50 : 80,
      render: (_value: unknown, record: VocabularyWord) => (
        <Popconfirm
          title="确定删除？"
          onConfirm={e => {
            if (e) e.stopPropagation();
            handleDeleteWord(record.id);
          }}
          onCancel={e => {
            if (e) e.stopPropagation();
          }}
          okText="确定"
          cancelText="取消"
        >
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={e => e.stopPropagation()}
            style={{ padding: '2px 4px', height: 'auto', fontSize: '12px' }}
          />
        </Popconfirm>
      ),
    });

    return baseColumns;
  };

  const rowSelection: TableProps<VocabularyWord>['rowSelection'] = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  return (
    <div className={styles.container}>
      <div className={styles.breadcrumb}>
        <a onClick={() => navigate('/vocabulary')}>
          <ArrowLeftOutlined />
          生词本
        </a>
      </div>

      <div className={styles.header}>
        <div className={styles.headerActions}>
          <div className={`${styles.actionGroup} ${styles.primary}`}>
            <Button
              type="primary"
              icon={<SoundOutlined />}
              onClick={() => {
                if (words.length > 0) {
                  setCurrentWordIndex(0);
                  setDetailVisible(true);
                } else {
                  message.info('生词本中还没有单词');
                }
              }}
            >
              开始学习
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => setAddWordModalVisible(true)}>
              添加单词
            </Button>
          </div>

          <div className={styles.actionGroup}>
            <Button icon={<DownloadOutlined />} onClick={handleExport}>
              导出生词本
            </Button>
            {selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`确定要删除选中的 ${selectedRowKeys.length} 个单词吗？`}
                onConfirm={handleBatchDelete}
                okText="确定"
                cancelText="取消"
              >
                <Button danger>删除选中 ({selectedRowKeys.length})</Button>
              </Popconfirm>
            )}
          </div>
        </div>
      </div>

      <Spin spinning={loading}>
        <Table<VocabularyWord>
          rowSelection={rowSelection}
          columns={getColumns()}
          dataSource={words}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showTotal: total => `共 ${total} 个单词`,
            responsive: true,
            showQuickJumper: false,
            size: 'small',
          }}
          onRow={(record: VocabularyWord) => ({
            onClick: () => handleWordClick(record),
            className: styles.tableRow,
          })}
          className={styles.wordTable}
          scroll={{ x: 'max-content' }}
          size="middle"
        />
      </Spin>

      <Modal
        title="编辑笔记"
        open={!!editingWord}
        onOk={handleEditNote}
        onCancel={() => {
          setEditingWord(null);
          setEditNote('');
        }}
        width={600}
      >
        <Input.TextArea
          value={editNote}
          onChange={e => setEditNote(e.target.value)}
          placeholder="请输入笔记"
          rows={4}
        />
      </Modal>

      <Modal
        title="添加单词"
        open={addWordModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setAddWordModalVisible(false);
          form.resetFields();
        }}
        width={700}
        className={styles.addWordModal}
        styles={{
          body: {
            padding: '24px 32px',
          },
        }}
      >
        <Form
          form={form}
          onFinish={handleAddWord}
          layout="vertical"
          requiredMark={false}
          size="large"
        >
          <div className={styles.formRow}>
            <Form.Item
              name="word"
              label="单词"
              rules={[{ required: true, message: '请输入单词' }]}
              className={styles.formItem}
            >
              <Input placeholder="请输入要添加的单词" />
            </Form.Item>
            <Form.Item name="phonetic" label="读音" className={styles.formItem}>
              <Input placeholder="例如：いえ" />
            </Form.Item>
            <Form.Item name="pos" label="词性" className={styles.formItemSmall}>
              <Select
                placeholder="选择词性"
                options={posOptions}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </div>

          <div className={styles.formRow}>
            <Form.Item name="meanings" label="释义" className={styles.formItem}>
              <Input.TextArea
                placeholder="请输入释义，多个释义请用；分隔（例如：你好；问候）"
                autoSize={{ minRows: 1, maxRows: 3 }}
              />
            </Form.Item>
          </div>

          <Form.Item name="example" label="例句">
            <Input.TextArea
              placeholder="请输入例句"
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{ fontSize: '14px' }}
            />
          </Form.Item>

          <Form.Item name="note" label="笔记">
            <Input.TextArea
              placeholder="添加笔记"
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{ fontSize: '14px' }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <WordDetail
        visible={detailVisible}
        onClose={() => setDetailVisible(false)}
        currentWord={currentWordIndex >= 0 ? words[currentWordIndex] : null}
        onPrevWord={currentWordIndex > 0 ? handlePrevWord : undefined}
        onNextWord={currentWordIndex < words.length - 1 ? handleNextWord : undefined}
        onUpdateNote={handleUpdateNote}
      />
    </div>
  );
};

export default WordList;
